import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Paper, LinearProgress, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Card, CardContent, useMediaQuery,
  CircularProgress, Grid, List,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import utils from '../../utils';

const CollaboraConNoi = () => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/api/collaborazionicli', {
          headers: utils.getAuthHeaders(),
        });
        const { success, error, clienti } = res.data;
        if (success) {
          setClienti(clienti.clienti_portati);
        } else {
          setError(error);
        }
      } catch (err) {
        setError('Si è verificato un errore durante il caricamento dei dati.');
        console.error(err);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const calculateProgress = (pagato, totale) => {
    return (pagato / totale) * 100;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const renderTable = () => {
    return (
      <TableContainer component={Paper} elevation={3}>
        <Table aria-label="tabella clienti portati">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#333333', color: '#FFFFFF' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Ragione Sociale</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Percentuale</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Importo Generato</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Compenso</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Importo Pagato</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Stato Pagamento</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clienti.map((cliente) => {
              const progressPercentage = calculateProgress(cliente.importoPagato, cliente.importoTotale * 0.01 * cliente.percentuale);
              return (
                <TableRow key={cliente.id}>
                  <TableCell>{cliente.ragioneSociale}</TableCell>
                  <TableCell>{cliente.percentuale}%</TableCell>
                  <TableCell>{formatCurrency(cliente.importoTotale)}</TableCell>
                  <TableCell>{formatCurrency(cliente.importoTotale * 0.01 * cliente.percentuale)}</TableCell>
                  <TableCell>{formatCurrency(cliente.importoPagato)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progressPercentage}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: progressPercentage === 100 ? 'success.main' : 'primary.main',
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">
                          {`${Math.round(progressPercentage)}%`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCards = () => {
    return (
      <List>
        {clienti.map((cliente) => {
          const progressPercentage = calculateProgress(cliente.importoPagato, cliente.importoTotale * 0.01 * cliente.percentuale);
          return (
            <Card key={cliente.id} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom>
                  {cliente.ragioneSociale}
                </Typography>
                <Divider sx={{ my: 1 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Percentuale:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {cliente.percentuale}%
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Importo Generato:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {formatCurrency(cliente.importoTotale)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Compenso:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {formatCurrency(cliente.importoTotale * 0.01 * cliente.percentuale)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Importo Pagato:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {formatCurrency(cliente.importoPagato)}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Stato Pagamento:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progressPercentage}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: progressPercentage === 100 ? 'success.main' : 'primary.main',
                          }
                        }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {`${Math.round(progressPercentage)}%`}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
                    {formatCurrency(cliente.importoPagato)} / {formatCurrency(cliente.importoTotale * 0.01 * cliente.percentuale)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </List>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Collabora con Noi!
        </Typography>

        <Typography variant="subtitle1" sx={{ fontStyle: 'italic', color: 'text.secondary', maxWidth: '80%', mx: 'auto' }}>
          Per ogni cliente che porti alla nostra azienda, ti riconosciamo una percentuale sul fatturato
          generato. Potrai monitorare facilmente le tue commissioni nella sezione sottostante,
          dove troverai tutti i dettagli sui clienti da te presentati, compresi gli importi dovuti e quelli già pagati.
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            Clienti Portati
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography variant="body1" color="error" sx={{ textAlign: 'center', py: 4 }}>
            {error}
          </Typography>
        ) : clienti.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            Non hai ancora portato alcun cliente. Inizia ora!
          </Typography>
        ) : (
          isLgUp ? renderTable() : renderCards()
        )}
      </Paper>

      <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
        Per maggiori informazioni sul programma "Collabora con Noi" contatta il nostro Studio anche direttamente in chat!.
      </Typography>
    </Container>
  );
};

export default CollaboraConNoi;
